/**
 * Review Workflow API Client
 * 
 * Provides TypeScript interfaces and functions for review workflow operations.
 * All functions use the API client from client.ts and handle errors appropriately.
 */

import { getAPIClient } from './client';

// TypeScript Interfaces
export interface ReviewAssignment {
  id: string;
  reviewer_id: string;
  reviewer_name?: string;
  review_type: string;
  assigned_by: string;
  assigned_by_name?: string;
  assigned_at: string;
  deadline?: string;
  status: 'pending' | 'in_progress' | 'completed' | 'cancelled';
  started_at?: string;
  completed_at?: string;
  decision?: 'approved' | 'rejected';
  notes?: string;
}

export interface RevisionRequest {
  id: string;
  review_assignment_id?: string;
  requested_by: string;
  requested_by_name?: string;
  section_reference?: string;
  issue_description: string;
  required_changes: string;
  priority: 'critical' | 'important' | 'optional';
  deadline?: string;
  status: 'open' | 'in_progress' | 'completed' | 'cancelled';
  completed_at?: string;
  completed_by?: string;
  completed_by_name?: string;
  created_at: string;
  updated_at: string;
}

export interface ReviewHistory {
  id: string;
  report_id: string;
  review_assignment_id?: string;
  action: 'submitted' | 'assigned' | 'started' | 'completed' | 'approved' | 'rejected' | 'revision_requested' | 're_submitted';
  performed_by: string;
  performed_by_name?: string;
  performed_at: string;
  notes?: string;
  metadata: Record<string, any>;
}

export interface ReviewTimeline {
  id: string;
  action: string;
  performed_by: string;
  performed_by_name: string;
  performed_at: string;
  notes?: string;
  metadata: Record<string, any>;
  assignment_id?: string;
}

export interface RevisionCycle {
  cycle_number: number;
  submitted_at: string;
  submitted_by: string;
  revision_requests: Array<{
    requested_at: string;
    requested_by: string;
    notes?: string;
    metadata?: Record<string, any>;
  }>;
  re_submitted_at?: string;
  re_submitted_by?: string;
}

export interface ReviewSubmissionStatus {
  submitted: boolean;
  submitted_at?: string;
  submitted_by?: string;
  revision_cycle_count: number;
  last_reviewed_at?: string;
  last_reviewed_by?: string;
  ready_for_submission: boolean;
  ready_for_re_submission: boolean;
  submission_validation: {
    ready: boolean;
    errors: string[];
    warnings: string[];
    writer_checklist?: any;
    content_length?: number;
    section_count?: number;
  };
  re_submission_validation: {
    ready: boolean;
    errors: string[];
    revision_status?: any;
  };
  active_assignments: number;
  total_assignments: number;
}

export interface AvailableReviewer {
  id: string;
  username: string;
  full_name?: string;
  expertise: string[];
  qualifications?: string[];
}

export type ReviewType = 'full' | 'quick' | 'technical' | 'compliance';

// Review Assignment Functions
export async function assignReviewer(
  reportId: string,
  reviewerId: string,
  reviewType: ReviewType,
  deadline?: string,
  notes?: string
): Promise<ReviewAssignment> {
  const client = getAPIClient();
  return client.post<ReviewAssignment>(`/reports/${reportId}/review/assign`, {
    reviewer_id: reviewerId,
    review_type: reviewType,
    deadline,
    notes,
  });
}

export async function getReviewAssignments(reportId: string): Promise<ReviewAssignment[]> {
  const client = getAPIClient();
  return client.get<ReviewAssignment[]>(`/reports/${reportId}/review/assignments`);
}

export async function getReviewAssignment(
  reportId: string,
  assignmentId: string
): Promise<ReviewAssignment> {
  const client = getAPIClient();
  return client.get<ReviewAssignment>(`/reports/${reportId}/review/assignments/${assignmentId}`);
}

export async function updateReviewAssignment(
  reportId: string,
  assignmentId: string,
  updates: {
    deadline?: string;
    notes?: string;
    status?: 'pending' | 'in_progress' | 'completed' | 'cancelled';
  }
): Promise<ReviewAssignment> {
  const client = getAPIClient();
  return client.patch<ReviewAssignment>(`/reports/${reportId}/review/assignments/${assignmentId}`, updates);
}

export async function deleteReviewAssignment(
  reportId: string,
  assignmentId: string
): Promise<{ deleted: boolean; assignment_id: string }> {
  const client = getAPIClient();
  return client.delete<{ deleted: boolean; assignment_id: string }>(
    `/reports/${reportId}/review/assignments/${assignmentId}`
  );
}

export async function startReview(
  reportId: string,
  assignmentId: string
): Promise<ReviewAssignment> {
  const client = getAPIClient();
  return client.post<ReviewAssignment>(
    `/reports/${reportId}/review/assignments/${assignmentId}/start`
  );
}

export async function completeReview(
  reportId: string,
  assignmentId: string,
  decision: 'approved' | 'rejected',
  notes?: string
): Promise<ReviewAssignment> {
  const client = getAPIClient();
  return client.post<ReviewAssignment>(
    `/reports/${reportId}/review/assignments/${assignmentId}/complete`,
    { decision, notes }
  );
}

export async function getAvailableReviewers(
  reportId: string,
  reviewType: ReviewType
): Promise<AvailableReviewer[]> {
  const client = getAPIClient();
  return client.get<AvailableReviewer[]>(
    `/reports/${reportId}/review/available-reviewers`,
    { params: { review_type: reviewType } }
  );
}

// Revision Request Functions
export async function createRevisionRequest(
  reportId: string,
  section: string | undefined,
  issue: string,
  changes: string,
  priority: 'critical' | 'important' | 'optional',
  deadline?: string,
  reviewAssignmentId?: string
): Promise<RevisionRequest> {
  const client = getAPIClient();
  return client.post<RevisionRequest>(`/reports/${reportId}/revisions`, {
    section_reference: section,
    issue_description: issue,
    required_changes: changes,
    priority,
    deadline,
    review_assignment_id: reviewAssignmentId,
  });
}

export async function getRevisionRequests(
  reportId: string,
  status?: 'open' | 'in_progress' | 'completed' | 'cancelled'
): Promise<RevisionRequest[]> {
  const client = getAPIClient();
  return client.get<RevisionRequest[]>(`/reports/${reportId}/revisions`, {
    params: status ? { status } : undefined,
  });
}

export async function getRevisionRequest(
  reportId: string,
  requestId: string
): Promise<RevisionRequest> {
  const client = getAPIClient();
  return client.get<RevisionRequest>(`/reports/${reportId}/revisions/${requestId}`);
}

export async function updateRevisionRequest(
  reportId: string,
  requestId: string,
  updates: {
    status?: 'open' | 'in_progress' | 'completed' | 'cancelled';
    issue_description?: string;
    required_changes?: string;
    priority?: 'critical' | 'important' | 'optional';
    deadline?: string;
  }
): Promise<RevisionRequest> {
  const client = getAPIClient();
  return client.patch<RevisionRequest>(`/reports/${reportId}/revisions/${requestId}`, updates);
}

export async function completeRevisionRequest(
  reportId: string,
  requestId: string
): Promise<RevisionRequest> {
  const client = getAPIClient();
  return client.patch<RevisionRequest>(`/reports/${reportId}/revisions/${requestId}/complete`);
}

export async function getPendingRevisions(reportId: string): Promise<RevisionRequest[]> {
  const client = getAPIClient();
  return client.get<RevisionRequest[]>(`/reports/${reportId}/revisions/pending`);
}

export async function getRevisionSummary(reportId: string): Promise<{
  completion_status: {
    all_completed: boolean;
    critical_completed: boolean;
    critical_pending: number;
    important_pending: number;
    optional_pending: number;
    total_pending: number;
    message: string;
  };
  counts_by_priority: {
    critical: { open: number; in_progress: number; completed: number; cancelled: number };
    important: { open: number; in_progress: number; completed: number; cancelled: number };
    optional: { open: number; in_progress: number; completed: number; cancelled: number };
  };
  total: number;
  pending: number;
}> {
  const client = getAPIClient();
  return client.get(`/reports/${reportId}/revisions/summary`);
}

// Review History Functions
export async function getReviewHistory(
  reportId: string,
  limit?: number
): Promise<ReviewHistory[]> {
  const client = getAPIClient();
  return client.get<ReviewHistory[]>(`/reports/${reportId}/review/history`, {
    params: limit ? { limit } : undefined,
  });
}

export async function getReviewTimeline(reportId: string): Promise<ReviewTimeline[]> {
  const client = getAPIClient();
  return client.get<ReviewTimeline[]>(`/reports/${reportId}/review/timeline`);
}

export async function getRevisionCycles(reportId: string): Promise<RevisionCycle[]> {
  const client = getAPIClient();
  return client.get<RevisionCycle[]>(`/reports/${reportId}/review/revision-cycles`);
}

// Review Submission Functions
export async function submitForReview(
  reportId: string,
  reviewType: ReviewType,
  notes?: string,
  reviewerId?: string
): Promise<{
  submitted: boolean;
  submitted_at: string;
  submitted_by: string;
  review_type: string;
  revision_cycle: number;
}> {
  const client = getAPIClient();
  return client.post(`/reports/${reportId}/review/submit`, {
    review_type: reviewType,
    notes,
    reviewer_id: reviewerId,
  });
}

export async function reSubmitForReview(
  reportId: string,
  revisionNotes?: string
): Promise<{
  re_submitted: boolean;
  re_submitted_at: string;
  re_submitted_by: string;
  revision_cycle: number;
}> {
  const client = getAPIClient();
  return client.post(`/reports/${reportId}/review/re-submit`, {
    revision_notes: revisionNotes,
  });
}

export async function getSubmissionStatus(reportId: string): Promise<ReviewSubmissionStatus> {
  const client = getAPIClient();
  return client.get<ReviewSubmissionStatus>(`/reports/${reportId}/review/submission-status`);
}

export async function validateReadiness(reportId: string): Promise<{
  ready: boolean;
  errors: string[];
  warnings: string[];
  writer_checklist?: any;
  content_length?: number;
  section_count?: number;
}> {
  const client = getAPIClient();
  return client.post(`/reports/${reportId}/review/validate-readiness`);
}
